import React, { useEffect } from 'react';
import { fromJS, Map } from 'immutable';

import { updateChannelMember } from 'flow/actions/channels-actions';
import { fetchExtension } from 'flow/actions/extension-actions';
import Toggle from 'flow/components/ui-components/Toggle';
import useAppContext from 'flow/hooks/useAppContext';
import useExtension from 'flow/hooks/useExtension';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import {
    selectExtensionActiveProfileKey,
    selectOwnExtensionKey,
} from 'flow/selectors/ExtensionSelectors';

const LiveChat = () => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const { ownExtension } = useExtension();
    const ownExtensionKey = useAppSelector(selectOwnExtensionKey);
    const extensionActiveProfileKey = useAppSelector((state) =>
        selectExtensionActiveProfileKey(state, ownExtensionKey),
    );

    const channelsMap = useAppSelector((state) =>
        state.channels.get('channels', Map()),
    );

    useEffect(() => {
        //Active profiles can in rare cases miss a profileKey
        if (!extensionActiveProfileKey) {
            // When we change our profiles, this call will be made,
            // however the data won't always have reached the DB, returning an old profile with this call.
            setTimeout(
                () => dispatch(fetchExtension(ownExtensionKey, 'live')),
                300,
            );
        }
    }, [dispatch, ownExtensionKey, extensionActiveProfileKey]);

    if (channelsMap.isEmpty()) {
        return null;
    }

    const myChannels = channelsMap.filter((channel) => {
        const members = channel.get('channelMemberDTOs');
        if (members === undefined) {
            return false;
        }

        return members
            .map((member) => member.get('extensionKey'))
            .contains(ownExtensionKey);
    });

    if (myChannels.isEmpty()) {
        return null;
    }

    const isLoggedIn = (channel) => {
        const channelMember = channel
            .get('channelMemberDTOs')
            .find(
                (member) =>
                    member.get('chatUserKey') ===
                    ownExtension.get('chatUserKey'),
            );

        return channelMember.get('loggedIn');
    };

    const toggle = (channel) => {
        const channelMember = channel
            .get('channelMemberDTOs')
            .find(
                (member) =>
                    member.get('chatUserKey') ===
                    ownExtension.get('chatUserKey'),
            );

        const channelMemberDTO = {
            key: channelMember.get('key'),
            loggedIn: !channelMember.get('loggedIn'),
        };

        dispatch(
            updateChannelMember(
                channel.get('key'),
                channelMember.get('key'),
                fromJS(channelMemberDTO),
            ),
        );
    };

    return (
        <>
            <h3 className="mt-4 mb-1 px-2 text-xs font-bold">
                {i18next.t('Profile.SegmentedControl.ChannelLogin.Title')}
            </h3>

            {myChannels
                .sort((a, b) =>
                    a.get('channelName').localeCompare(b.get('channelName')),
                )
                .map((channel) => (
                    <div
                        className="flex h-10 items-center justify-between rounded-sm p-2 hover:bg-gray-200"
                        key={channel.get('key')}
                    >
                        <p>{channel.get('channelName')}</p>
                        <Toggle
                            height={20}
                            width={32}
                            onChange={() => toggle(channel)}
                            value={isLoggedIn(channel)}
                            text={channel.get('channelName')}
                        />
                    </div>
                ))
                .valueSeq()}
        </>
    );
};

export default LiveChat;
